/**
 * Who holds which 2026 pick, RIGHT NOW — the build-time ledger plus every trade settled since.
 *
 * `ownership2026` is emitted from a data file at build time and was the only answer the app had,
 * which meant a trade settled through the Trades page moved a pick in the ledger and nowhere
 * else: the draftboard, the keeper resolver and the trade form itself all went on believing the
 * seller held it until somebody edited the file and deployed. Eight days before the draft, with
 * deals being filed, that is a board that lies a little more each day.
 *
 * So the base map is layered with `pickTransfersFrom` over the settled live trades, the same way
 * the mock layers hypothetical ones, and every screen that asks the question asks this hook.
 * `version` ticks when a trade settles, for callers that memoise on the answer.
 */

import { useEffect, useMemo, useState } from 'react';
import { applyPickTrades } from '../domain/board.js';
import { gmOfTeam } from '../domain/identity.js';
import { ownership2026 } from '../domain/picks2026.js';
import { pickTransfersFrom } from '../domain/tradeKeepers.js';
import type { LeagueStore, LiveTradeDoc } from '../store/types.js';

const SEASON = 2026;

export function useOwnership(store: LeagueStore): {
  ownership: Map<string, string>;
  /** The settled trades the map was built from, for a screen that wants to say so. */
  settled: LiveTradeDoc[];
  version: number;
} {
  const [trades, setTrades] = useState<LiveTradeDoc[]>([]);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let live = true;
    const load = () => store.liveTrades(SEASON).then((t) => {
      if (!live) return;
      setTrades(t);
      setVersion((v) => v + 1);
    }).catch(() => { /* a store without live trades has nothing to layer */ });
    void load();
    const off = store.subscribe(SEASON, (what) => { if (what === 'liveTrades') void load(); });
    return () => { live = false; off(); };
  }, [store]);

  const settled = useMemo(
    () => trades.filter((t) => t.settledAs && !t.withdrawn && t.season === SEASON),
    [trades],
  );
  const ownership = useMemo(
    () => applyPickTrades(ownership2026(gmOfTeam), pickTransfersFrom(settled, SEASON)),
    [settled],
  );
  return { ownership, settled, version };
}
